import type { CancelToken } from 'axios';
import { ref } from 'vue';
import type { IScore } from '@/interface/IScore';
import type {
    IDynamicMarketTicket,
    IOutrightTicket,
    ISingleTicket,
    Ticket,
    VoucherCapableTicket,
} from '@/interface/ITicket';
import type {
    BetPageType,
    OpenTicketStatus,
    PlaceOrderStatus,
    PriceStyle,
    StatementDetailStatus,
} from '@/interface/enum';
import {
    MarketType,
    OpenTicketStatus as OpenTicketStatusValue,
    PlaceOrderStatus as PlaceOrderStatusValue,
    SportType,
    TicketType,
} from '@/interface/enum';
import type {
    DynamicMarketOrderRequest,
    OutrightTicketInfo,
    PlaceOutrightOrderRequest,
    PlaceSingleOrderRequest,
    TicketInfo,
} from '@/interface/pollux';
import type { IVoucher } from '@/interface/voucher';
import Api from '@/libraries/api';
import { assertNever, getTicketPrice } from '@/libraries/betSlip/ticketHelper';
import { createTicketVoucherInput } from '@/libraries/composables/useSingleModeVoucher';
import { useVoucher } from '@/libraries/composables/useVoucher';

export interface IOpenTicketResult {
    key: string;
    isSuccess: boolean;
    uid: string;
    minBet: number;
    maxBet: number;
    balance: number | null;
    price: number;
    priceStyle: PriceStyle;
    point: number;
    betCondition: string | null;
    score: IScore;
    errorStatus: OpenTicketStatus;
    rawVouchers: IVoucher[] | null;
}

export interface IPlaceTicketResult {
    key: string;
    isSuccess: boolean;
    uid: string;
    balance: number | null;
    price: number;
    placeOrderStatus: PlaceOrderStatus;
    orderStatus: StatementDetailStatus | null;
    message: string;
    extraInfo?: any;
}

export async function openTicket(
    ticket: Ticket,
    cancelToken: CancelToken,
): Promise<IOpenTicketResult> {
    switch (ticket.type) {
        case TicketType.Single:
            return openSingleTicket(ticket, cancelToken);
        case TicketType.DynamicMarket:
            return openDynamicMarketTicket(ticket, cancelToken);
        case TicketType.Outright:
            return openOutrightTicket(ticket, cancelToken);
        default:
            return assertNever(ticket);
    }
}

export interface IPlaceTicketOrder {
    ticket: Ticket;
    voucherId: string | null;
}

export async function placeTickets(
    orders: IPlaceTicketOrder[],
): Promise<IPlaceTicketResult[]> {
    const singleOrders = orders.filter(isSingleTicketOrder);
    const dynamicMarketOrders = orders.filter(isDynamicMarketTicketOrder);
    const outrightOrders = orders.filter(isOutrightTicketOrder);

    const resultsByKey = new Map<string, IPlaceTicketResult>();

    if (singleOrders.length > 0) {
        const singleResults = await placeSingleTicketsBatch(singleOrders);
        singleResults.forEach(result => resultsByKey.set(result.key, result));
    }
    if (dynamicMarketOrders.length > 0) {
        const dynamicMarketResults = await placeDynamicMarketTicketsBatch(dynamicMarketOrders);
        dynamicMarketResults.forEach(result => resultsByKey.set(result.key, result));
    }
    if (outrightOrders.length > 0) {
        const outrightResults = await placeOutrightTicketsBatch(outrightOrders);
        outrightResults.forEach(result => resultsByKey.set(result.key, result));
    }

    return orders
        .map(order => resultsByKey.get(order.ticket.key))
        .filter((result): result is IPlaceTicketResult => result !== undefined);
}

interface ISingleTicketOrder extends IPlaceTicketOrder {
    ticket: ISingleTicket;
}

interface IDynamicMarketTicketOrder extends IPlaceTicketOrder {
    ticket: IDynamicMarketTicket;
}

interface IOutrightTicketOrder extends IPlaceTicketOrder {
    ticket: IOutrightTicket;
}

function isSingleTicketOrder(order: IPlaceTicketOrder): order is ISingleTicketOrder {
    return order.ticket.type === TicketType.Single;
}

function isDynamicMarketTicketOrder(order: IPlaceTicketOrder): order is IDynamicMarketTicketOrder {
    return order.ticket.type === TicketType.DynamicMarket;
}

function isOutrightTicketOrder(order: IPlaceTicketOrder): order is IOutrightTicketOrder {
    return order.ticket.type === TicketType.Outright;
}

async function placeSingleTicketsBatch(
    orders: ISingleTicketOrder[],
): Promise<IPlaceTicketResult[]> {
    const requests = orders.map(({ ticket, voucherId }) => createSinglePlaceBetRequest(ticket, voucherId));
    const tickets = orders.map(order => order.ticket);
    try {
        const responses = await Api.placeSingleBets(requests);
        return tickets.map((ticket) => {
            const response = responses.find(r => r.oddsId === ticket.oddsId && r.option === ticket.option);
            return response === undefined
                ? createPlaceTicketFailureResult(ticket)
                : normalizePlaceTicketResult(ticket, response);
        });
    } catch (error) {
        console.error('Failed to place Single Tickets', error);
        return tickets.map(createPlaceTicketFailureResult);
    }
}

async function placeOutrightTicketsBatch(
    orders: IOutrightTicketOrder[],
): Promise<IPlaceTicketResult[]> {
    const tickets = orders.map(order => order.ticket);
    const requests = tickets.map(createOutrightPlaceBetRequest);
    try {
        const responses = await Api.placeOutrightBets(requests);
        return tickets.map((ticket) => {
            const response = responses.find(r => r.oddsId === ticket.id);
            return response === undefined
                ? createPlaceTicketFailureResult(ticket)
                : normalizePlaceTicketResult(ticket, response);
        });
    } catch (error) {
        console.error('Failed to place Outright Tickets', error);
        return tickets.map(createPlaceTicketFailureResult);
    }
}

async function placeDynamicMarketTicketsBatch(
    orders: IDynamicMarketTicketOrder[],
): Promise<IPlaceTicketResult[]> {
    const requests = orders.map(({ ticket, voucherId }) => createDynamicMarketPlaceBetRequest(ticket, voucherId));
    const tickets = orders.map(order => order.ticket);
    try {
        const responses = await Api.placeDynamicMarketBets(requests);
        return tickets.map((ticket) => {
            const response = responses.find(r => r.selectionId === ticket.selection.id);
            return response === undefined
                ? createPlaceTicketFailureResult(ticket)
                : normalizePlaceTicketResult(ticket, response);
        });
    } catch (error) {
        console.error('Failed to place Dynamic Market Tickets', error);
        return tickets.map(createPlaceTicketFailureResult);
    }
}

function createPlaceTicketFailureResult(
    ticket: Ticket,
): IPlaceTicketResult {
    return {
        key: ticket.key,
        isSuccess: false,
        uid: ticket.uid,
        balance: null,
        price: getTicketPrice(ticket),
        placeOrderStatus: PlaceOrderStatusValue.GeneralFailure,
        orderStatus: null,
        message: '',
    };
}

async function fetchVouchersForTicket(ticket: VoucherCapableTicket): Promise<IVoucher[] | null> {
    const { fetchVouchers, rawVoucher } = useVoucher(ref(createTicketVoucherInput(ticket)));
    await fetchVouchers();
    return rawVoucher.value;
}

async function openSingleTicket(
    ticket: ISingleTicket,
    cancelToken: CancelToken,
): Promise<IOpenTicketResult> {
    const [response, rawVouchers] = await Promise.all([
        Api.openMultipleSingleTicket(createSingleOpenTicketRequest(ticket), { cancelToken }),
        fetchVouchersForTicket(ticket),
    ]);
    const oddsInfo = response.oddsInfo[`${ticket.oddsId}-${ticket.option}`];
    const errorStatus = oddsInfo?.errorCode ?? response.errorCode;
    return {
        key: ticket.key,
        isSuccess: oddsInfo !== undefined
            && errorStatus === OpenTicketStatusValue.Success,
        uid: oddsInfo?.uid ?? '',
        minBet: response.minBet,
        maxBet: response.maxBet,
        balance: response.balance,
        price: oddsInfo?.price ?? ticket.price,
        priceStyle: oddsInfo?.priceStyle ?? ticket.priceStyle,
        point: oddsInfo?.point ?? ticket.odds.point,
        betCondition: oddsInfo?.betCondition ?? ticket.odds.betCondition,
        score: {
            home: oddsInfo?.liveHomeScore ?? ticket.odds.eventResult.liveHomeScore,
            away: oddsInfo?.liveAwayScore ?? ticket.odds.eventResult.liveAwayScore,
        },
        errorStatus,
        rawVouchers,
    };
}

export interface IOpenParlaySubBetResult {
    key: string;
    isSuccess: boolean;
    errorStatus: OpenTicketStatus;
    price: number;
    priceStyle: PriceStyle;
    point: number;
    betCondition: string | null;
    score: IScore;
}

export interface IOpenParlayResult {
    isSuccess: boolean;
    uid: string;
    minBet: number;
    maxBet: number;
    balance: number | null;
    errorStatus: OpenTicketStatus;
    subBetResults: IOpenParlaySubBetResult[];
}

export async function openParlayTicket(
    subBets: ISingleTicket[],
    cancelToken: CancelToken,
): Promise<IOpenParlayResult> {
    const response = await Api.openMixParlayTicket(
        subBets.map(createSingleOpenTicketRequest),
        { cancelToken },
    );
    const subBetResults = subBets.map((ticket) => {
        const oddsInfo = response.oddsInfo?.[`${ticket.oddsId}-${ticket.option}`];
        let errorStatus = oddsInfo?.errorCode ?? response.errorCode;
        if (oddsInfo === undefined && errorStatus === OpenTicketStatusValue.Success) {
            errorStatus = OpenTicketStatusValue.OddsClosed;
        }
        return {
            key: ticket.key,
            isSuccess: oddsInfo !== undefined
                && errorStatus === OpenTicketStatusValue.Success,
            errorStatus,
            price: oddsInfo?.price ?? ticket.price,
            priceStyle: oddsInfo?.priceStyle ?? ticket.priceStyle,
            point: oddsInfo?.point ?? ticket.odds.point,
            betCondition: oddsInfo?.betCondition ?? ticket.odds.betCondition,
            score: {
                home: oddsInfo?.liveHomeScore ?? ticket.odds.eventResult.liveHomeScore,
                away: oddsInfo?.liveAwayScore ?? ticket.odds.eventResult.liveAwayScore,
            },
        };
    });
    return {
        isSuccess: response.oddsInfo !== undefined
            && response.errorCode === OpenTicketStatusValue.Success,
        uid: response.mpUid,
        minBet: response.minBet,
        maxBet: response.maxBet,
        balance: response.balance ?? null,
        errorStatus: response.errorCode,
        subBetResults,
    };
}

export interface IPlaceParlayOrder {
    subBets: ISingleTicket[];
    subBetResults: Record<string, IOpenParlaySubBetResult>;
    stake: number;
    uid: string;
    selectedCombo: number[];
    betPage: BetPageType;
}

export interface IPlaceParlayResult {
    isSuccess: boolean;
    placeOrderStatus: PlaceOrderStatus;
    orderStatus: StatementDetailStatus | null;
    message: string;
    extraInfo?: any;
}

export async function placeParlayTicket(order: IPlaceParlayOrder): Promise<IPlaceParlayResult> {
    const mpBet = order.subBets.map((ticket) => {
        const subBetResult = order.subBetResults[ticket.key];
        return {
            eventId: ticket.eventId,
            marketType: MarketType.toPollux(ticket.odds.marketType),
            oddsId: ticket.oddsId,
            option: ticket.option,
            leagueId: ticket.event.leagueId,
            point: subBetResult?.point ?? ticket.odds.point,
            liveScore: {
                home: subBetResult?.score.home ?? ticket.odds.eventResult.liveHomeScore,
                away: subBetResult?.score.away ?? ticket.odds.eventResult.liveAwayScore,
            },
        };
    });
    const response = await Api.placeComboParlayBet({
        stake: order.stake,
        uid: order.uid,
        mpBet,
        selectedCombo: order.selectedCombo,
        betPage: order.betPage,
    });
    return {
        isSuccess: response.isPlaceBetSuccess,
        placeOrderStatus: response.placeOrderStatus,
        orderStatus: response.orderStatus ?? null,
        message: response.message ?? '',
        extraInfo: response.extraInfo,
    };
}

async function openDynamicMarketTicket(
    ticket: IDynamicMarketTicket,
    cancelToken: CancelToken,
): Promise<IOpenTicketResult> {
    const [response, rawVouchers] = await Promise.all([
        Api.openMultipleDynamicMarketTicket(ticket.selection.id, { cancelToken }),
        fetchVouchersForTicket(ticket),
    ]);
    return {
        key: ticket.key,
        isSuccess: response.error.code === OpenTicketStatusValue.Success
            && response.selection !== null,
        uid: response.uid,
        minBet: response.minBet,
        maxBet: response.maxBet,
        balance: response.balance,
        price: response.selection?.price?.value ?? getTicketPrice(ticket),
        priceStyle: response.selection?.price?.priceStyle ?? ticket.priceStyle,
        point: 0,
        betCondition: response.selection?.betCondition ?? ticket.selection.betCondition,
        score: response.selection?.score ?? {
            home: ticket.match.liveHomeScore,
            away: ticket.match.liveAwayScore,
        },
        errorStatus: response.error.code,
        rawVouchers,
    };
}

async function openOutrightTicket(
    ticket: IOutrightTicket,
    cancelToken: CancelToken,
): Promise<IOpenTicketResult> {
    const response = await Api.openMultipleOutrightTicket(createOutrightOpenTicketRequest(ticket), { cancelToken });
    const oddsInfo = response.oddsInfo[`${ticket.id}-or`];
    const errorStatus = oddsInfo?.errorCode ?? response.errorCode;
    return {
        key: ticket.key,
        isSuccess: oddsInfo !== undefined
            && errorStatus === OpenTicketStatusValue.Success,
        uid: oddsInfo?.uid ?? '',
        minBet: response.minBet,
        maxBet: response.maxBet,
        balance: response.balance,
        price: oddsInfo?.price ?? ticket.price,
        priceStyle: oddsInfo?.priceStyle ?? ticket.priceStyle,
        point: oddsInfo?.point ?? 0,
        betCondition: oddsInfo?.betCondition ?? null,
        score: {
            home: oddsInfo?.liveHomeScore ?? 0,
            away: oddsInfo?.liveAwayScore ?? 0,
        },
        errorStatus,
        rawVouchers: [],
    };
}

function createSingleOpenTicketRequest(ticket: ISingleTicket): TicketInfo {
    return {
        oddsId: ticket.oddsId,
        option: ticket.option,
        sportType: SportType.toPollux(ticket.event.sportType),
        marketType: MarketType.toPollux(ticket.odds.marketType),
        eventId: ticket.eventId,
        isLive: ticket.odds.isLive,
    };
}

function createOutrightOpenTicketRequest(ticket: IOutrightTicket): OutrightTicketInfo {
    return {
        oddsId: ticket.id,
        option: 'or',
        marketType: MarketType.toPollux(MarketType.OutRight),
        sportType: SportType.toPollux(ticket.sportType),
        eventId: ticket.id,
        leagueId: ticket.leagueId,
        isLive: ticket.isLive,
    };
}

function createSinglePlaceBetRequest(
    ticket: ISingleTicket,
    voucherId: string | null,
): PlaceSingleOrderRequest {
    return {
        eventId: ticket.eventId,
        oddsId: ticket.oddsId,
        option: ticket.option,
        stake: ticket.stake,
        uid: ticket.uid,
        betPage: ticket.betPage,
        sportType: SportType.toPollux(ticket.event.sportType),
        marketType: MarketType.toPollux(ticket.odds.marketType),
        point: ticket.odds.point,
        liveScore: {
            home: ticket.odds.eventResult.liveHomeScore,
            away: ticket.odds.eventResult.liveAwayScore,
        },
        voucherIdString: voucherId ?? '',
    };
}

function createDynamicMarketPlaceBetRequest(
    ticket: IDynamicMarketTicket,
    voucherId: string | null,
): DynamicMarketOrderRequest {
    return {
        betPage: ticket.betPage,
        matchId: ticket.match.matchId,
        selectionId: ticket.selection.id,
        stake: ticket.stake,
        uid: ticket.uid,
        voucherId: Number.parseInt(voucherId ?? ''),
    };
}

function createOutrightPlaceBetRequest(
    ticket: IOutrightTicket,
): PlaceOutrightOrderRequest {
    return {
        leagueId: ticket.leagueId,
        outrightId: ticket.id,
        stake: ticket.stake,
        uid: ticket.uid,
        betPage: ticket.betPage,
        sportType: SportType.toPollux(ticket.sportType),
        isLive: ticket.isLive,
    };
}

function normalizePlaceTicketResult(
    ticket: Ticket,
    response: {
        isPlaceBetSuccess: boolean;
        placeOrderStatus: PlaceOrderStatus;
        orderStatus: StatementDetailStatus;
        price?: number;
        message?: string;
        extraInfo?: any;
    },
): IPlaceTicketResult {
    return {
        key: ticket.key,
        isSuccess: response.isPlaceBetSuccess,
        uid: ticket.uid,
        balance: null,
        price: response.price ?? getTicketPrice(ticket),
        placeOrderStatus: response.placeOrderStatus,
        orderStatus: response.orderStatus,
        message: response.message ?? '',
        extraInfo: response.extraInfo,
    };
}
